import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from 'react-intl';
import PropTypes from 'prop-types';

// material-ui
import Avatar from '@mui/material/Avatar';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardActionArea from '@mui/material/CardActionArea';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Grid from '@mui/material/Grid';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { useTheme, alpha } from '@mui/material/styles';

// ant design icons
import UserOutlined from '@ant-design/icons/UserOutlined';
import WifiOutlined from '@ant-design/icons/WifiOutlined';
import ClockCircleOutlined from '@ant-design/icons/ClockCircleOutlined';

// project imports
import MainCard from 'components/MainCard';
import useOnlineStatus from 'hooks/useOnlineStatus';

// ==============================|| OFFLINE PROFILE PICKER ||============================== //

const OfflineProfilePicker = ({ onSelect }) => {
  const intl = useIntl();
  const theme = useTheme();
  const navigate = useNavigate();
  const { availableOfflineUsers, selectOfflineUser, isOnline, dbInitialized, refreshOfflineProfiles } = useOnlineStatus();

  const [selecting, setSelecting] = useState(false);

  // Refresh profiles on mount to ensure we have the latest from IndexedDB
  useEffect(() => {
    if (dbInitialized) {
      refreshOfflineProfiles();
    }
  }, [dbInitialized, refreshOfflineProfiles]);

  // Offline mode is therapist-only. Filter out stale non-therapist rows that may
  // linger in IndexedDB from before this restriction existed.
  const therapistProfiles = (availableOfflineUsers || []).filter((p) => (p.role || 'therapist') === 'therapist');

  const handleUserSelect = async (profile) => {
    if (selecting) return;
    setSelecting(true);

    const success = await selectOfflineUser(profile.id);

    if (success) {
      onSelect?.();
      navigate('/offline/my-calendar');
    }
    setSelecting(false);
  };

  const getInitials = (name) => {
    if (!name) return '?';
    return name
      .split(' ')
      .map((n) => n[0])
      .join('')
      .toUpperCase()
      .slice(0, 2);
  };

  const formatLastLogin = (dateStr) => {
    if (!dateStr) return intl.formatMessage({ id: 'offline.profilePicker.unknown' });
    try {
      const date = new Date(dateStr);
      const now = new Date();
      const diffMs = now - date;
      const diffDays = Math.floor(diffMs / (1000 * 60 * 60 * 24));

      if (diffDays === 0) return intl.formatMessage({ id: 'offline.profilePicker.today' });
      if (diffDays === 1) return intl.formatMessage({ id: 'offline.profilePicker.yesterday' });
      if (diffDays < 7) return intl.formatMessage({ id: 'offline.profilePicker.daysAgo' }, { count: diffDays });
      return date.toLocaleDateString();
    } catch {
      return intl.formatMessage({ id: 'offline.profilePicker.unknown' });
    }
  };

  // DB still initializing — show loading spinner
  if (!dbInitialized) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
          <CircularProgress size={48} />
          <Typography variant="h5" textAlign="center">
            {intl.formatMessage({ id: 'offline.profilePicker.loading' })}
          </Typography>
        </Stack>
      </MainCard>
    );
  }

  // No profiles at all
  if (therapistProfiles.length === 0) {
    return (
      <MainCard>
        <Stack spacing={3} alignItems="center" sx={{ py: 6 }}>
          <Box
            sx={{
              width: 80,
              height: 80,
              borderRadius: '50%',
              bgcolor: alpha(theme.palette.warning.main, 0.1),
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center'
            }}
          >
            <WifiOutlined style={{ fontSize: '2.5rem', color: theme.palette.warning.main }} />
          </Box>
          <Typography variant="h4" textAlign="center">
            {intl.formatMessage({ id: 'offline.profilePicker.noProfiles' })}
          </Typography>
          <Typography variant="body1" color="text.secondary" textAlign="center" sx={{ maxWidth: 450 }}>
            {intl.formatMessage({ id: 'offline.profilePicker.noProfilesDescription' })}
          </Typography>
          {isOnline && (
            <Button variant="contained" onClick={() => navigate('/login')} startIcon={<UserOutlined />}>
              {intl.formatMessage({ id: 'offline.profilePicker.goToLogin' })}
            </Button>
          )}
        </Stack>
      </MainCard>
    );
  }

  // Profile grid
  return (
    <Box>
      <MainCard
        sx={{
          mb: 3,
          background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
          color: 'white'
        }}
      >
        <Stack spacing={1}>
          <Typography variant="h3" sx={{ fontWeight: 700 }}>
            {intl.formatMessage({ id: 'offline.profilePicker.title' })}
          </Typography>
          <Typography variant="body1" sx={{ opacity: 0.9 }}>
            {intl.formatMessage({ id: 'offline.profilePicker.subtitle' })}
          </Typography>
        </Stack>
      </MainCard>

      <Grid container spacing={3}>
        {therapistProfiles.map((profile) => (
          <Grid size={{ xs: 12, sm: 6, md: 4, lg: 3 }} key={profile.id}>
            <Card
              sx={{
                height: '100%',
                transition: 'all 0.2s ease',
                '&:hover': {
                  transform: 'translateY(-4px)',
                  boxShadow: theme.shadows[8]
                }
              }}
            >
              <CardActionArea onClick={() => handleUserSelect(profile)} disabled={selecting} sx={{ height: '100%' }}>
                <CardContent sx={{ textAlign: 'center', py: 4 }}>
                  <Avatar
                    sx={{
                      width: 72,
                      height: 72,
                      bgcolor: theme.palette.primary.main,
                      fontSize: '1.75rem',
                      fontWeight: 700,
                      mx: 'auto',
                      mb: 2
                    }}
                  >
                    {getInitials(profile.name)}
                  </Avatar>
                  <Typography variant="h5" sx={{ mb: 0.5 }}>
                    {profile.name}
                  </Typography>
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                    {profile.email}
                  </Typography>
                  <Stack direction="row" spacing={1} justifyContent="center" alignItems="center">
                    <ClockCircleOutlined style={{ fontSize: '0.75rem', color: theme.palette.text.secondary }} />
                    <Typography variant="caption" color="text.secondary">
                      {intl.formatMessage({ id: 'offline.profilePicker.lastLogin' })} {formatLastLogin(profile.lastLoginAt)}
                    </Typography>
                  </Stack>
                </CardContent>
              </CardActionArea>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};

OfflineProfilePicker.propTypes = {
  onSelect: PropTypes.func
};

export default OfflineProfilePicker;
